import { Stack } from '@mui/material'

import { Maybe, RecentViewedItemsQuery } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { MagazineItem, MagazineItemSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  artistId: string
  items: Maybe<RecentViewedItemsQuery['recentlyStoreViewedItems']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const RecentViewedItemsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('recentViewedItems')
  const { routerNames, routerPages, generatePath } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.RecentViewedItems} />

      <BaseLayout
        margin
        header={{
          back: true,
          title: t('title'),
          defaultBackPage: generatePath(routerPages.ArtistTop, {
            artistId:
              config.applicationType === 'standalone'
                ? config.artistId
                : state.artistId,
          }),
        }}
      >
        <ScrollFetcher
          nodes={state.items?.edges.map(({ node }, index) => (
            <MagazineItem
              key={node.id}
              item={node.item}
              divider={
                !!state.items?.edges.length &&
                index < state.items?.edges.length - 1
              }
              conductorName="recentryStoreViewList"
            />
          ))}
          componentProps={{ spacing: 2 }}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          emptyMessage={t('emptyMessage')}
          loadingComponent={
            <Stack spacing={2} sx={{ pt: 2 }}>
              {Array.from(Array(5).keys()).map(index => (
                <MagazineItemSkeleton key={index} divider={index < 4} />
              ))}
            </Stack>
          }
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
